/* eslint-disable react-hooks/exhaustive-deps */
import {
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { GetUserResponseType } from '../../utils/types/auth'

import { useNavigate } from 'react-router-dom'
import { createContext, useContextSelector } from 'use-context-selector'
import Loader from '../../components/common/loader'
import { getAccountFromMiddleLayer } from '../../services/syndeo-dashboard/user'
import { UserRole } from '../../utils/constants'
import { ROUTES } from '../../utils/constants/enum'
import { isAxiosError } from '../../utils/helper-functions'
import { getLocalState, useLocalState } from '../../utils/local-storage'

import { IUserData } from '../../utils/types/common'
import { UserDataContext } from './UserDataProvider'

interface DashboardContextType {
    userDataLoading: boolean
    userData: GetUserResponseType | undefined
    userRole: UserRole | undefined
    getUserDetail: () => Promise<void>
    showMLOptIn: boolean
    setShowMLOptIn: any
    showMLOptInStatus: boolean
    setShowMLOptInStatus: any
    showOptIn: boolean
    setShowOptIn: any
}

export const DashboardContext = createContext<DashboardContextType>(
    {} as DashboardContextType
)

const useUserData = () => {
    const [loading, setLoading] = useState(false)
    const { setUserData } = useContext(UserDataContext)
    // const [userData, setUserData] = useState<GetUserResponseType>()
    // const [userRole, setUserRole] = useState<UserRole>()
    const navigate = useRef(useNavigate())
    const [, , removeState] = useLocalState('auth-token')

    const getUserDetail = useCallback(async () => {
        setLoading(true)
        try {
            const response = await getAccountFromMiddleLayer()
            setUserData((prev: IUserData) => ({ ...prev, ...response }))
        } catch (error) {
            removeState()
            if (isAxiosError(error)) {
                if (error.response?.status === 404) {
                    navigate.current(ROUTES.SIGNUP)
                    return
                }
            }
            navigate.current(`${ROUTES.SIGNIN}`)
        } finally {
            setLoading(false)
        }
    }, [removeState])

    // useEffect(() => {
    //     setUserRole(userData?.userRole)
    // }, [userData])

    return [loading, getUserDetail] as const
}

export const DashboardProvider: FC<PropsWithChildren<unknown>> = ({
    children,
}) => {
    const [isLoading, getUserDetail] = useUserData()
    const [showOptIn, setShowOptIn] = useState(false)
    const [showMLOptIn, setShowMLOptIn] = useState(false)
    const [showMLOptInStatus, setShowMLOptInStatus] = useState(false)
    const { userData: userInfo } = useContext<any>(UserDataContext)
    const navigate = useNavigate()
    const {
        acceptTerms,
        appUserKey,
        birthDate: dateOfBirth,
        businessName,
        country,
        createDate,
        email: emailAddress,
        firstName,
        lastName,
        mobile: mobilePhoneNumber,
        optInMarketingByEmail,
        optInMarketingByPhone,
        postalCode,
        profilePicture: profileImageURL,
        subscribeEvents,
        userRole,
        username,
        loyaltyOptInB2B,
        loyaltyOptInB2B2C,
        beautyHealthCustomerId,
        optInInputOnlyLogin,
        allProvidersOptIn,
        multiAccount,
        memberId,
    } = userInfo ?? {}

    const userData = {
        acceptTerms,
        appUserKey,
        dateOfBirth,
        businessName,
        country,
        createDate,
        emailAddress,
        firstName,
        lastName,
        mobilePhoneNumber,
        optInMarketingByEmail,
        optInMarketingByPhone,
        postalCode,
        profileImageURL,
        subscribeEvents,
        userRole,
        username,
        loyaltyOptInB2B,
        loyaltyOptInB2B2C,
        beautyHealthCustomerId,
        optInInputOnlyLogin,
        allProvidersOptIn,
        multiAccount,
        memberId,
    }
    useEffect(() => {
        if (!userData.firstName) {
            getUserDetail()
        }
    }, [])
    useEffect(() => {
        const auth = getLocalState('auth-token')
        if (!auth) {
            navigate(`${ROUTES.SIGNIN}`)
        }
    }, [])
    // return null
    // const [loading, userData, userRole, getUserDetail] = useUserData()

    const mappedValue = useMemo(
        () => ({
            userData,
            userRole,
            getUserDetail,
            userDataLoading: isLoading,
            showMLOptIn,
            setShowMLOptIn,
            showMLOptInStatus,
            setShowMLOptInStatus,
            showOptIn,
            setShowOptIn,
        }),
        [
            userInfo,
            getUserDetail,
            isLoading,
            showOptIn,
            showMLOptIn,
            showMLOptInStatus,
        ]
    )
    return (
        <DashboardContext.Provider value={mappedValue}>
            {isLoading ? <Loader /> : <>{children}</>}
        </DashboardContext.Provider>
    )
}

export const useDashboardContext = <T extends unknown>(
    selector: (value: DashboardContextType) => T
): T => useContextSelector(DashboardContext, selector)
